import { toUserMessage } from '../../lib/errors.js';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToast,
  IonToggle,
  IonToolbar,
} from '@ionic/react';
import { closeOutline, trashOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useActiveBusiness } from '../../lib/auth/useActiveBusiness.js';
import { useEnqueueMaster } from '../../lib/data/queries.js';
import { useCelebrate } from '../CelebrationToast.js';

// T-149: frequenze selezionabili per la pulizia auto-generata dell'attrezzatura.
// Allineate al check constraint di cleaning_schedules.frequency e alle 3 matrici
// del registro mensile (giornaliera/settimanale/mensile sono quelle che hanno
// una sezione dedicata nel PDF; le altre restano valide ma confluiscono nelle
// pulizie a cadenza più lunga).
const SCHEDULE_FREQUENCIES: { value: string; label: string }[] = [
  { value: 'giornaliera', label: 'Giornaliera' },
  { value: 'settimanale', label: 'Settimanale' },
  { value: 'mensile', label: 'Mensile' },
  { value: 'trimestrale', label: 'Trimestrale' },
  { value: 'semestrale', label: 'Semestrale' },
  { value: 'annuale', label: 'Annuale' },
  { value: 'a_chiamata', label: 'A chiamata' },
];

export interface EquipmentEditorValue {
  id: string;
  name: string;
  kind: string;
  version?: number;
}

const KINDS: { value: string; label: string }[] = [
  { value: 'superficie', label: 'Superficie' },
  { value: 'attrezzo',   label: 'Attrezzo' },
  { value: 'area',       label: 'Area' },
  { value: 'utensile',   label: 'Utensile' },
  { value: 'altro',      label: 'Altro' },
];

export interface EquipmentEditorModalProps {
  isOpen: boolean;
  /** Se presente → modalità Modifica. */
  initial?: EquipmentEditorValue;
  /** Pre-compila il campo nome quando l'editor è aperto da una search vuota. */
  initialName?: string;
  onDismiss: () => void;
  onSaved?: (saved: EquipmentEditorValue) => void;
  /** Solo modalità edit: se passato, mostra "Archivia". */
  onRequestArchive?: (rec: { id: string; name: string; version: number }) => void;
  /**
   * T-149: quando true (solo in creazione), mostra l'opzione "Crea attività di
   * sanificazione" pre-attivata, che genera una cleaning_schedules collegata
   * all'attrezzatura → la fa comparire nel registro mensile.
   * Default false: quando l'editor è usato come sub-modale dentro l'editor di
   * un'attività di pulizia (CleaningTaskEditorModal), la schedule è già gestita
   * dall'attività stessa, quindi NON dobbiamo crearne una seconda (duplicato).
   * La pagina "Attrezzature pulizia" lo passa a true.
   */
  autoCreateSchedule?: boolean;
}

interface InternalForm {
  id: string;
  name: string;
  kind: string;
  version: number;
  // T-149: in creazione, genera anche una pulizia ricorrente (cleaning_schedule)
  // collegata a questa attrezzatura, così compare subito nel registro mensile
  // senza dover rifirmare il manuale. Solo per nuove attrezzature.
  createSchedule: boolean;
  scheduleFrequency: string;
}

function buildInitialState(
  initial: EquipmentEditorValue | undefined,
  initialName: string | undefined,
  autoCreateSchedule: boolean,
): InternalForm {
  if (initial) {
    return {
      id: initial.id,
      name: initial.name,
      kind: initial.kind ?? 'attrezzo',
      version: initial.version ?? 0,
      // In modifica non rigeneriamo schedule (eviterebbe duplicati): il toggle
      // è disattivato e non mostrato.
      createSchedule: false,
      scheduleFrequency: 'giornaliera',
    };
  }
  return {
    id: '',
    name: initialName ?? '',
    kind: 'attrezzo',
    version: 0,
    createSchedule: autoCreateSchedule,
    scheduleFrequency: 'giornaliera',
  };
}

export function EquipmentEditorModal(props: EquipmentEditorModalProps): JSX.Element {
  const { isOpen, initial, initialName, onDismiss, onSaved, onRequestArchive, autoCreateSchedule = false } = props;
  const { profile } = useAuth();
  void profile;
  const createMaster = useEnqueueMaster();
  const activeBusiness = useActiveBusiness();

  const [form, setForm] = useState<InternalForm>(() => buildInitialState(initial, initialName, autoCreateSchedule));
  const [toast, setToast] = useState<string | null>(null);
  const celebrate = useCelebrate();

  useEffect(() => {
    if (isOpen) setForm(buildInitialState(initial, initialName, autoCreateSchedule));
  }, [isOpen, initial, initialName, autoCreateSchedule]);

  async function save(): Promise<void> {
    const ctx = await activeBusiness.resolve();
    if (!ctx) {
      setToast('Nessun locale attivo');
      return;
    }
    const name = form.name.trim();
    if (!name) { setToast('Nome richiesto'); return; }
    const id = form.id || crypto.randomUUID();
    const isUpdate = !!form.id;
    try {
      await createMaster.mutateAsync({
        table: 'cleaning_equipments', id, op: isUpdate ? 'update' : 'insert', base_version: form.version ?? 0,
        patch: {
          id,
          tenant_id: ctx.tenantId,
          business_id: ctx.businessId,
          name,
          kind: form.kind,
        },
      });
      // T-149: alla creazione di una NUOVA attrezzatura, genera anche una
      // pulizia ricorrente collegata (cleaning_schedules.equipment_id = id).
      // È questo il collegamento che fa comparire l'attrezzatura come riga
      // nelle matrici Giornaliere/Settimanali/Mensili del registro mensile
      // (export-cleaning-month) e fa sì che ogni "Fatta" venga spuntata
      // giorno per giorno. Senza questo, l'attrezzatura aggiunta dopo la
      // firma del manuale restava fuori dal registro fino alla ri-firma.
      if (!isUpdate && form.createSchedule) {
        const scheduleId = crypto.randomUUID();
        await createMaster.mutateAsync({
          table: 'cleaning_schedules',
          id: scheduleId,
          op: 'insert',
          base_version: 0,
          patch: {
            id: scheduleId,
            tenant_id: ctx.tenantId,
            business_id: ctx.businessId,
            title: `Sanificazione ${name}`,
            frequency: form.scheduleFrequency,
            equipment_id: id,
            equipment_ids: [id],
            requires_photo: false,
            responsible_role: 'Personale di turno',
            instructions: 'Detersione + disinfezione + risciacquo come da manuale HACCP.',
          },
        });
      }
      celebrate(isUpdate ? 'Attrezzatura aggiornata' : 'Attrezzatura creata');
      onSaved?.({
        id,
        name,
        kind: form.kind,
        version: (form.version ?? 0) + 1,
      });
      onDismiss();
    } catch (err: unknown) {
      setToast(toUserMessage(err));
    }
  }

  return (
    <>
      <IonModal data-testid="equipment-editor" isOpen={isOpen} onDidDismiss={onDismiss}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{form.id ? 'Modifica attrezzatura' : 'Nuova attrezzatura'}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={onDismiss} aria-label="chiudi">
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
          <div style={{ padding: '12px 16px 24px' }}>
            <h2 style={{ margin: '4px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              🔧 Attrezzatura
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '4px 0', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel position="stacked">Nome *</IonLabel>
                <IonInput
                  value={form.name}
                  placeholder="Mocio cucina"
                  onIonInput={(ev) => setForm((f) => ({ ...f, name: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
            </div>

            <h2 style={{ margin: '20px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              🏷️ Tipo
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '12px 14px', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {KINDS.map((k) => {
                  const active = form.kind === k.value;
                  return (
                    <button
                      key={k.value}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, kind: k.value }))}
                      style={{
                        border: `1px solid ${active ? '#7c3aed' : '#cbd5e1'}`,
                        background: active ? '#7c3aed' : '#fff',
                        color: active ? '#fff' : '#475569',
                        borderRadius: 999,
                        padding: '5px 11px',
                        fontSize: 12,
                        fontWeight: active ? 600 : 500,
                        cursor: 'pointer',
                      }}
                    >
                      {k.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {!form.id && autoCreateSchedule && (
              <>
                <h2 style={{ margin: '20px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  🧽 Pulizia nel registro
                </h2>
                <div style={{ background: '#fff', borderRadius: 14, padding: '4px 0', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
                  <IonItem lines={form.createSchedule ? 'full' : 'none'} style={{ '--background': 'transparent' } as React.CSSProperties}>
                    <IonLabel style={{ whiteSpace: 'normal' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                        Crea attività di sanificazione
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                        Genera una pulizia ricorrente collegata: l'attrezzatura
                        comparirà nel registro mensile e potrai spuntarla ogni
                        giorno.
                      </div>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={form.createSchedule}
                      onIonChange={(ev) => setForm((f) => ({ ...f, createSchedule: ev.detail.checked }))}
                      aria-label="Crea attività di sanificazione"
                    />
                  </IonItem>
                  {form.createSchedule && (
                    <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                      <IonLabel>Frequenza</IonLabel>
                      <IonSelect
                        value={form.scheduleFrequency}
                        interface="popover"
                        onIonChange={(ev) => setForm((f) => ({ ...f, scheduleFrequency: String(ev.detail.value) }))}
                      >
                        {SCHEDULE_FREQUENCIES.map((freq) => (
                          <IonSelectOption key={freq.value} value={freq.value}>
                            {freq.label}
                          </IonSelectOption>
                        ))}
                      </IonSelect>
                    </IonItem>
                  )}
                </div>
              </>
            )}

            <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
              <IonButton
                expand="block"
                onClick={() => void save()}
                style={{ '--border-radius': '12px', flex: 2, height: 46 } as React.CSSProperties}
              >
                {form.id ? 'Salva modifiche' : 'Crea attrezzatura'}
              </IonButton>
              <IonButton
                expand="block"
                fill="outline"
                onClick={onDismiss}
                style={{ '--border-radius': '12px', flex: 1, height: 46 } as React.CSSProperties}
              >
                Annulla
              </IonButton>
            </div>
            {form.id && onRequestArchive && (
              <IonButton
                expand="block"
                color="danger"
                fill="clear"
                style={{ '--border-radius': '12px', marginTop: 8 } as React.CSSProperties}
                onClick={() => {
                  onRequestArchive({ id: form.id, name: form.name, version: form.version });
                  onDismiss();
                }}
              >
                <IonIcon icon={trashOutline} slot="start" />
                Archivia attrezzatura
              </IonButton>
            )}
          </div>
        </IonContent>
      </IonModal>
      <IonToast isOpen={!!toast} message={toast ?? ''} duration={1500} onDidDismiss={() => setToast(null)} />
    </>
  );
}
